import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Products } from '@/pages/Products';
import PackageEditDialog from '@/components/common/PackageEditDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PIN maestro de bootstrap. Vive sólo como constante local; el backend
// valida y rechaza cualquier valor distinto a `025600`.
const BOOTSTRAP_PIN = '025600';

/**
 * /edit — página privada de edición del catálogo. Sólo yo conozco la URL;
 * esa es toda la seguridad. Al abrirla:
 *   1. Si no estoy en sesión admin, hace POST a /public/admin-pin-login con
 *      el PIN maestro y refresca la sesión (checkAuth).
 *   2. Muestra una grilla con los MISMOS productos/paquetes del catálogo
 *      público, pero cada tarjeta trae ✏️ (editar) y 🗑️ (borrar).
 *   3. Barra flotante con ➕ Producto / ➕ Paquete.
 *   4. Al editar abre los dialogs reutilizables inline, SIN navegar.
 *
 * Nota: no se linkea desde ningún menú ni footer. Es una URL "secreta".
 */
export default function EditCatalog() {
  const { user, checkAuth } = useAuth();
  const [bootstrapState, setBootstrapState] = useState('bootstrapping'); // 'bootstrapping' | 'ready' | 'error'
  const [bootstrapMsg, setBootstrapMsg] = useState('Entrando en modo edición…');
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'promo' | 'products' | 'packages'
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [creatingPackage, setCreatingPackage] = useState(false);

  // Paso 1: asegurar sesión admin (silent login si falta)
  useEffect(() => {
    let cancel = false;
    const run = async () => {
      try {
        if (user?.role === 'admin') {
          if (!cancel) setBootstrapState('ready');
          return;
        }
        setBootstrapMsg('Activando sesión de edición…');
        await axios.post(`${API}/public/admin-pin-login`, { pin: BOOTSTRAP_PIN }, { withCredentials: true });
        if (checkAuth) await checkAuth();
        if (!cancel) {
          setBootstrapState('ready');
          toast.success('🔓 Modo edición activo');
        }
      } catch (e) {
        if (cancel) return;
        setBootstrapState('error');
        const s = e?.response?.status;
        if (s === 429) setBootstrapMsg('Demasiados intentos. Espera 15 min.');
        else if (s === 404) setBootstrapMsg('Backend aún instalando el endpoint. Espera a que termine el deploy.');
        else setBootstrapMsg('No se pudo activar el modo edición.');
      }
    };
    run();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  // Paso 2: cargar catálogo
  useEffect(() => {
    if (bootstrapState !== 'ready') return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/catalog?include_hidden=1&_=${Date.now()}`, { withCredentials: true });
        if (!cancel) setItems(data?.items || []);
      } catch (e) {
        if (!cancel) toast.error('No se pudo cargar el catálogo');
      }
    })();
    return () => { cancel = true; };
  }, [bootstrapState, refreshKey]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (filter === 'promo' && !it.has_active_promo) return false;
      if (filter === 'products' && it.kind === 'package') return false;
      if (filter === 'packages' && it.kind !== 'package') return false;
      if (!q) return true;
      const t = `${it.name || ''} ${it.sku || ''} ${it.brand || ''}`.toLowerCase();
      return t.includes(q);
    });
  }, [items, query, filter]);

  const openEdit = async (item) => {
    try {
      if (item.kind === 'package') {
        const { data } = await axios.get(`${API}/packages/${item.id}`, { withCredentials: true });
        setEditingPackage(data);
      } else {
        setEditingProduct({ id: item.id });
      }
    } catch (_) {
      toast.error('No se pudo cargar el item');
    }
  };

  const handleDelete = async (item) => {
    const label = item.kind === 'package' ? 'paquete' : 'producto';
    if (!window.confirm(`¿Borrar ${label} "${item.name}"? No se puede deshacer.`)) return;
    try {
      if (item.kind === 'package') await axios.delete(`${API}/packages/${item.id}`, { withCredentials: true });
      else                          await axios.delete(`${API}/products/${item.id}`, { withCredentials: true });
      toast.success(`${label === 'paquete' ? 'Paquete' : 'Producto'} eliminado`);
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const handleSaved = () => setRefreshKey(k => k + 1);

  if (bootstrapState !== 'ready') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-3">{bootstrapState === 'error' ? '⚠️' : '🔓'}</div>
          <h1 className="text-lg font-semibold text-slate-800 mb-2">Modo edición</h1>
          <p className="text-sm text-slate-500" data-testid="edit-bootstrap-msg">{bootstrapMsg}</p>
        </div>
      </div>
    );
  }

  const promoCount = items ? items.filter(x => x.has_active_promo).length : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-24">
      {/* Header */}
      <header className="bg-slate-900 text-white shadow" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}>
        <div className="max-w-5xl mx-auto px-4 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Modo edición privado</p>
              <h1 className="text-xl sm:text-2xl font-bold mt-0.5">🔓 Editor de catálogo</h1>
              <p className="text-xs opacity-75 mt-1">
                {items ? `${items.length} items · ${promoCount} en promo` : 'Cargando…'}
              </p>
            </div>
            <a href="/catalogo" className="text-[11px] text-slate-300 hover:text-white underline underline-offset-2" data-testid="edit-view-public">
              Ver público
            </a>
          </div>

          <div className="mt-3 flex gap-2">
            <input
              type="search"
              placeholder="🔍 Buscar por nombre, SKU o marca…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-white/95 text-slate-900 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="edit-search"
            />
          </div>

          <div className="mt-2 flex gap-1 rounded-lg bg-white/10 p-1 text-xs">
            {[
              { k: 'all', label: 'Todos' },
              { k: 'products', label: 'Productos' },
              { k: 'packages', label: 'Paquetes' },
              { k: 'promo', label: `🏷️ En promo${promoCount ? ` (${promoCount})` : ''}` },
            ].map(opt => (
              <button
                key={opt.k}
                onClick={() => setFilter(opt.k)}
                className={`flex-1 px-2 py-1.5 rounded-md font-medium ${filter === opt.k ? 'bg-white text-slate-800' : 'text-white'}`}
                data-testid={`edit-filter-${opt.k}`}
              >{opt.label}</button>
            ))}
          </div>
        </div>
      </header>

      {/* Grid */}
      <main className="max-w-5xl mx-auto px-3 py-4">
        {items === null && (
          <div className="text-center text-sm text-slate-500 py-10">Cargando catálogo…</div>
        )}
        {items && filtered.length === 0 && (
          <div className="text-center text-sm text-slate-500 py-10">Sin resultados</div>
        )}
        {items && filtered.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="edit-grid">
            {filtered.map((it) => (
              <EditCard key={`${it.kind}-${it.id}`} item={it} onEdit={openEdit} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </main>

      {/* Barra admin flotante */}
      <div className="fixed left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 bg-slate-900/95 text-white px-3 py-2 rounded-full shadow-lg border border-slate-700" style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)' }}>
        <span className="text-[11px] font-semibold text-emerald-400">🔓 EDIT</span>
        <button
          onClick={() => setCreatingProduct(true)}
          className="text-xs font-medium bg-emerald-600 hover:bg-emerald-700 rounded-full px-3 py-1.5 active:scale-95 transition"
          data-testid="edit-add-product"
        >➕ Producto</button>
        <button
          onClick={() => setCreatingPackage(true)}
          className="text-xs font-medium bg-violet-600 hover:bg-violet-700 rounded-full px-3 py-1.5 active:scale-95 transition"
          data-testid="edit-add-package"
        >➕ Paquete</button>
      </div>

      {/* Dialogs */}
      {(editingProduct || creatingProduct) && (
        <Products
          embedded
          embedEditId={editingProduct?.id || null}
          onEmbedClose={() => { setEditingProduct(null); setCreatingProduct(false); setTimeout(handleSaved, 800); }}
        />
      )}
      {(editingPackage || creatingPackage) && (
        <PackageEditDialog
          open={true}
          onClose={() => { setEditingPackage(null); setCreatingPackage(false); }}
          pkg={editingPackage || null}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function EditCard({ item, onEdit, onDelete }) {
  const hasPromo = item.has_active_promo && item.promo_price > 0;
  const discount = hasPromo && item.base_price > item.promo_price
    ? Math.round((1 - item.promo_price / item.base_price) * 100)
    : 0;

  return (
    <article className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col hover:shadow-md transition" data-testid={`edit-card-${item.id}`}>
      <div className="relative aspect-square bg-slate-100">
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl text-slate-300">{item.kind === 'package' ? '🎁' : '📦'}</div>
        )}

        {discount > 0 && (
          <div className="absolute top-2 left-2 bg-red-600 text-white text-[11px] font-bold rounded-full px-2 py-0.5 shadow">
            -{discount}%
          </div>
        )}
        {item.kind === 'package' && (
          <div className="absolute bottom-2 left-2 bg-violet-600 text-white text-[10px] font-bold rounded-full px-2 py-0.5 shadow">
            PAQUETE
          </div>
        )}

        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onEdit(item); }}
          className="absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-white border border-slate-200 shadow flex items-center justify-center active:scale-90 transition"
          title="Editar"
          data-testid={`edit-card-edit-${item.id}`}
        >
          <span className="text-base leading-none">✏️</span>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(item); }}
          className="absolute top-12 right-2 z-10 w-9 h-9 rounded-full bg-rose-50 hover:bg-rose-100 border border-rose-200 shadow flex items-center justify-center active:scale-90 transition"
          title="Borrar"
          data-testid={`edit-card-delete-${item.id}`}
        >
          <span className="text-base leading-none">🗑️</span>
        </button>
      </div>

      <div className="p-2.5 flex-1 flex flex-col gap-1">
        <h3 className="font-semibold text-sm text-slate-800 line-clamp-2 leading-tight">{item.name}</h3>
        {item.sku && <p className="text-[10px] font-mono text-slate-400">{item.sku}</p>}
        <div className="mt-1 flex items-baseline gap-1.5">
          {hasPromo ? (
            <>
              <span className="font-bold text-emerald-700 text-base">${item.promo_price}</span>
              <span className="text-xs text-slate-400 line-through">${item.base_price}</span>
            </>
          ) : (
            <span className="font-bold text-slate-800 text-base">${item.base_price}</span>
          )}
        </div>
      </div>
    </article>
  );
}
